import React from "react";
import PropTypes from "prop-types";
import Button from "components/Common/Button";
import classnames from "classnames";
import colors from "stylesheets/utilities/colors.scss";
import "./style.scss";

/**
 * Higher Order Component
 * @param {*} WrapperComponent
 */
function dialogWrapper(WrapperComponent) {
  class Dialog extends React.Component {
    /**
     * Constructor
     *
     * @param {Object} props
     */
    constructor(props) {
      super(props);

      // Cannot user removeEventListener unless function is bound in constructor: https://gist.github.com/Restuta/e400a555ba24daa396cc
      this.handleClickOutside = this.handleClickOutside.bind(this);
      this.handleKeyPressOutside = this.handleKeyPressOutside.bind(this);
      this.closeDialog = this.closeDialog.bind(this);
      this.defaultPosition = {
        left: 0,
        top: 0
      };

      this.state = {
        position: this.defaultPosition
      };
    }

    /**
     * componentDidMount
     */
    componentDidMount() {
      const position = this.getDialogPosition();

      this.setState({
        position
      });
    }


    /**
     * componentDidUpdate
     * @param {Object} prevProps
     */
    componentDidUpdate(prevProps) {
      const {
        position,
        isOpen
      } = this.props;

      if (prevProps.position !== position ||
        prevProps.isOpen !== isOpen) {
        const newPosition = this.getDialogPosition();

        this.setState({
          position: newPosition
        });
      }

      if (prevProps.isOpen !== isOpen) {
        if (isOpen) {
          document.addEventListener("click", this.handleClickOutside, true);
          document.addEventListener(
            "keydown",
            this.handleKeyPressOutside,
            true
          );
        } else {
          this.removeEventListeners();
        }
      }
    }

    /**
     * componentWillUnmount
     */
    componentWillUnmount() {
      this.removeEventListeners();
    }

    /**
     * Calculates dropdown position based on the button's location in the DOM
     *
     * @returns {Object}
     */
    getDialogPosition() {
      const {
        position,
        alignment
      } = this.props;
      let left = null;
      let top = null;

      if (position !== null) {
        // this is the position of the dropdown
        top = position.top + 24;
        // the default alignment of the object is to the right.
        // In order to change this, given the 248px width of the dropdown
        // 123px is distance the dropdown should be shifted from left: 0, top: 0
        // to ensure that it's aligned to the left by default instead of the right.
        left = position.left - 123;

        if (alignment === "left") {
          // Doesn't make a lot of sense
          // eslint-disable-next-line prefer-destructuring
          left = position.left;
        }
      }

      return {
        left,
        top
      };
    }

    /**
     * removeEventListeners
     */
    removeEventListeners() {
      document.removeEventListener("click", this.handleClickOutside, true);
      document.removeEventListener("keydown", this.handleKeyPressOutside, true);
    }

    /**
     * closeDialog
     *
     * @param {Object} e
     */
    closeDialog(e) {
      const { closeDialog } = this.props;
      closeDialog(e.target);
    }

    /**
     * handleClickOutside
     *
     * @param {Event} e
     */
    handleClickOutside(e) {
      const { closeDialog } = this.props;

      if (this.container && !this.container.contains(e.target)) {
        e.preventDefault();
        closeDialog(e.target);
      }
    }

    /**
     * handleKeyPressOutside
     *
     * @param {Event} e
     */
    handleKeyPressOutside(e) {
      const { closeDialog } = this.props;

      if (e.keyCode === 27) {
        closeDialog();
      }
    }

    /**
     * render
     *
     * @returns {React}
     */
    render() {
      const {
        customClassName,
        isOpen,
        title,
        alignment,
        width,
        greetingId
      } = this.props;
      const { position } = this.state;

      if (isOpen) {
        return (
          <div
            className={classnames("Dialog", {
              [customClassName]: customClassName
            })}
            ref={(container) => {
              this.container = container;
            }}
            style={{
              width,
              [alignment]: 0,
              top: position.top,
              left: position.left
            }}
          >
            <h3 className="Dialog__title">{title}</h3>
            {!greetingId && (
              <Button
                icon="Close"
                title="Close Dialog"
                clear
                onClick={this.closeDialog}
                size="x-small"
                fillColor={colors.colorGrey4}
                customClassName="Dialog__close"
                showActiveBackground
              />
            )}
            <WrapperComponent {...this.props} />
          </div>
        );
      }

      return null;
    }
  }

  Dialog.propTypes = {
    closeDialog: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    title: PropTypes.string,
    alignment: PropTypes.string,
    position: PropTypes.shape({
      top: PropTypes.number,
      left: PropTypes.number
    }),
    width: PropTypes.number,
    inModal: PropTypes.bool,
    absolute: PropTypes.bool
  };

  Dialog.defaultProps = {
    width: 200,
    absolute: false,
    inModal: false,
    position: null,
    alignment: null,
    title: null
  };

  return Dialog;
}

export default dialogWrapper;
